
import React from "react";
import { Card, CardContent } from "../../components/ui/card";
import { formatValue, displayLineItemName } from "../../lib/utils";

interface ProfitLossTabProps {
  financialData: any;
}

const ProfitLossTab: React.FC<ProfitLossTabProps> = ({ financialData }) => {
  const financials = financialData || [];
  const years = financials.map((f: any) => {
    const year = f.year.split('-')[0];
    return `FY ${(parseInt(year) - 1).toString()} - ${year.toString().slice(-2)}`;
  });

  const nature = financials.find((f: any) => f.nature)?.nature || null;
  
  // Detect if this is LLPIN data
  const isLLPIN = financials.some((f: any) => 
    (f as any).isLLPIN || 
    (f as any).statement_of_assets_and_liabilities || 
    (f as any).statement_of_income_and_expenditure
  );

  // P&L line items in order
  const pnlLineItems = [
    'net_revenue',
    'total_cost_of_materials_consumed',
    'total_purchases_of_stock_in_trade',
    'total_changes_in_inventories_or_finished_goods',
    'total_employee_benefit_expense',
    'total_other_expenses',
    'operating_profit',
    'other_income',
    'depreciation',
    'profit_before_interest_and_tax',
    'interest',
    'profit_before_tax_and_exceptional_items_before_tax',
    'exceptional_items_before_tax',
    'profit_before_tax',
    'income_tax',
    'profit_for_period_from_continuing_operations',
    'profit_from_discontinuing_operation_after_tax',
    'minority_interest_and_profit_from_associates_and_joint_ventures',
    'profit_after_tax'
  ];

  // Revenue breakup line items
  const revenueBreakupItems = [
    'revenue_from_operations',
    'revenue_from_interest',
    'revenue_from_other_financial_services',
    'revenue_from_sale_of_products',
    'revenue_from_sale_of_services',
    'other_operating_revenues',
    'excise_duty',
    'service_tax_collected',
    'other_duties_taxes_collected'
  ];

  const renderFinancialTable = (lineItems: string[], dataSource: string, title: string, subtitle?: string) => (
    <div className="mb-8">
      <h4 className="text-lg font-semibold mb-2 text-gray-800">{title}</h4>
      {subtitle && <p className="text-sm text-gray-600 mb-4">{subtitle}</p>}
      <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
        <div className="w-full overflow-x-auto">
          <div
            className="grid"
            style={{
              minWidth: `${240 + years.length * 160}px`, // dynamically adjust min-width
              gridTemplateColumns: `minmax(240px,1fr) repeat(${years.length}, minmax(160px,1fr))`,
            }}
          >
            {/* Header Row */}
            <div className="bg-gray-50 p-4 font-semibold border-b border-r sticky left-0 z-20">
              Line Item
            </div>
            {years.map((year: any, index: any) => (
              <div key={index} className="bg-gray-50 p-4 font-semibold text-center border-b border-r">
                {year}
              </div>
            ))}

            {/* Data Rows */}
            {lineItems.map((lineItem) => (
              <React.Fragment key={lineItem}>
                {/* Line Item Name (Frozen Column) */}
                <div
                  className={`p-4 border-b border-r sticky left-0 z-10 bg-white ${lineItem.includes('profit') || lineItem.includes('total') || lineItem.includes('operating_profit')
                    ? 'font-semibold bg-blue-50'
                    : ''
                    }`}
                >
                  {displayLineItemName(lineItem, isLLPIN)}
                </div>

                {/* Values for each year */}
                {financials.map((financial: any, colIndex: number) => {
                  let value;
                  if (dataSource === 'lineItems' && !isLLPIN) {
                    value = financial.pnl?.lineItems?.[lineItem];
                  } else if (dataSource === 'revenue_breakup') {
                    value = financial.pnl?.revenue_breakup?.[lineItem];
                  } else if (dataSource === 'lineItems' && isLLPIN) {
                    value = financial.statement_of_income_and_expenditure?.lineItems?.[lineItem];
                  } else {
                    value = financial.pnl?.subTotals?.[lineItem];
                  }

                  return (
                    <div
                      key={`${lineItem}-${colIndex}`}
                      className={`p-4 text-right border-b border-r ${lineItem.includes('profit') || lineItem.includes('total') || lineItem.includes('operating_profit')
                        ? 'font-semibold bg-blue-50'
                        : ''
                        }`}
                    >
                      {formatValue(value)}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Profit & Loss Statement</h3>
          {/* <p className="truncate break-words max-w-xs hover:overflow-visible hover:whitespace-normal hover:break-words">
            Financial Years: {years.join(', ')}
          </p> */}
             <h3>{nature}</h3>
        </div>

        {/* Main P&L Section */}
        {renderFinancialTable(
          pnlLineItems,
          'lineItems',
          '📊 Profit & Loss Statement',
          'Complete profit and loss statement with all line items'
        )}

        {/* Summary Cards
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h5 className="font-semibold text-green-800 mb-2">Latest Revenue</h5>
            <p className="text-2xl font-bold text-green-700">
              {formatValue(financials[0]?.pnl?.lineItems?.net_revenue)}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="font-semibold text-blue-800 mb-2">Operating Profit</h5>
            <p className="text-2xl font-bold text-blue-700">
              {formatValue(financials[0]?.pnl?.lineItems?.operating_profit)}
            </p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h5 className="font-semibold text-purple-800 mb-2">Profit Before Tax</h5>
            <p className="text-2xl font-bold text-purple-700">
              {formatValue(financials[0]?.pnl?.lineItems?.profit_before_tax)}
            </p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h5 className="font-semibold text-orange-800 mb-2">Profit After Tax</h5>
            <p className="text-2xl font-bold text-orange-700">
              {formatValue(financials[0]?.pnl?.lineItems?.profit_after_tax)}
            </p>
          </div>
        </div> */}
      </CardContent>
    </Card>
  );
};

export default ProfitLossTab;
